import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { format, parse, isValid, type Locale } from 'date-fns';
import * as locales from 'date-fns/locale';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
  useController,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

import { Icon } from '$/components/common/Icon';
import { useLocalizationStore } from '$/components/core/Localization/hooks/useLocalizationStore';
import { lodashGet } from '$/utils/lodash';

interface HookFormDatePickerProps<T extends FieldValues> {
  accessor: FieldPath<T>;
  label: string;
  registerOptions?: RegisterOptions<T>;
  minDate?: Date;
}

export const HookFormDatePicker = <T extends FieldValues>({
  accessor,
  label,
  registerOptions,
  minDate,
}: HookFormDatePickerProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const language = useLocalizationStore.useLanguage();

  const locale: Locale =
    (locales as Record<string, Locale>)[language] ?? locales.enUS;

  const {
    field,
    formState: { errors },
  } = useController<T>({ name: accessor, rules: registerOptions });

  const selectedDate = field.value
    ? new Date(field.value as string)
    : undefined;
  const isInvalid = lodashGet(errors, accessor) != null;
  const borderSettings = isInvalid ? '2px solid' : undefined;
  const borderColor = isInvalid ? 'form.errorBorder' : undefined;
  const [inputValue, setInputValue] = useState(
    selectedDate ? format(selectedDate, 'dd.MM.yyyy') : '',
  );
  const [month, setMonth] = useState(new Date());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, ''); // nur Ziffern
    let formatted = raw;

    if (raw.length > 2 && raw.length <= 4) {
      formatted = `${raw.slice(0, 2)}.${raw.slice(2)}`;
    } else if (raw.length > 4) {
      formatted = `${raw.slice(0, 2)}.${raw.slice(2, 4)}.${raw.slice(4, 8)}`;
    }

    setInputValue(formatted);

    // Erst wenn vollständig (TT.MM.JJJJ), ins Formular schreiben
    if (raw.length === 8) {
      const parsed = parse(formatted, 'dd.MM.yyyy', new Date());
      if (isValid(parsed)) {
        field.onChange(format(parsed, 'yyyy-MM-dd'));
        setMonth(parsed);
        field.onBlur();
      } else {
        field.onChange('');
        field.onBlur();
      }
    } else {
      field.onChange('');
      field.onBlur();
    }
  };
  const handleDaySelect = (date?: Date) => {
    field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
    setInputValue(date ? format(date, 'dd.MM.yyyy') : '');
    if (date) {
      setMonth(date);
    }
    setIsOpen(false);
  };
  const startMonth = minDate ?? new Date(new Date().getFullYear(), 0);
  const endMonth = new Date(startMonth.getFullYear() + 10, 11);

  return (
    <FormControl isInvalid={isInvalid}>
      <HStack alignItems='baseline' justifyContent='space-between'>
        <FormLabel color='text' fontSize='sm' whiteSpace='nowrap'>
          {label}
        </FormLabel>
        <FormErrorMessage mt='0' color='form.errorText'>
          {lodashGet(errors, accessor)?.message?.toString()}
        </FormErrorMessage>
      </HStack>

      <Popover
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          field.onBlur();
        }}
        placement='bottom-start'
      >
        <PopoverTrigger>
          <InputGroup>
            <Input
              pr='2rem'
              border={borderSettings}
              borderColor={borderColor}
              maxLength={10}
              onBlur={field.onBlur}
              onChange={handleInputChange}
              placeholder='TT.MM.JJJJ'
              value={inputValue}
            ></Input>
            <InputRightElement w='fit-content' onClick={() => setIsOpen(true)}>
              <Icon w='18px' icon='calendar' mr='4' color='lighterText' />
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent w='fit-content' bg='background'>
          <PopoverBody>
            <DayPicker
              mode='single'
              selected={selectedDate}
              locale={locale}
              onSelect={handleDaySelect}
              disabled={minDate ? { before: minDate } : undefined}
              defaultMonth={selectedDate ?? minDate}
              captionLayout='dropdown'
              startMonth={startMonth}
              endMonth={endMonth}
              month={month}
              onMonthChange={setMonth}
              style={
                {
                  '--rdp-accent-color':
                    'var(--chakra-colors-primaryButton-background)',
                  '--rdp-accent-background-color':
                    'var(--chakra-colors-primaryBlue-100)',
                } as React.CSSProperties
              }
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};
